import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, combineLatest, merge, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Artist, ArtistSearchResult, Genre, Label, ParticipantRole, Release, ReleaseParticipant, Store, Track, TrackParticipant } from './releases.types';
import { PaginatedData, Pagination } from 'app/shared/shared.types';
import { ReleaseSubscription } from '../payments/payments.types';

@Injectable({
    providedIn: 'root'
})
export class ReleasesService {
    private _release: BehaviorSubject<Release | null> = new BehaviorSubject(null);
    private _releases: BehaviorSubject<Release[] | null> = new BehaviorSubject(null);
    private _artists: BehaviorSubject<Artist[] | null> = new BehaviorSubject(null);
    private _paginatedArtists: BehaviorSubject<PaginatedData<Artist> | null> = new BehaviorSubject(null);
    private _paginatedLabels: BehaviorSubject<PaginatedData<Label> | null> = new BehaviorSubject(null);
    private _labels: BehaviorSubject<Label[] | null> = new BehaviorSubject(null);
    private _genres: BehaviorSubject<Genre[] | null> = new BehaviorSubject(null);
    private _stores: BehaviorSubject<Store[] | null> = new BehaviorSubject(null);
    private _pagination: BehaviorSubject<Pagination | null> = new BehaviorSubject(null);



    constructor(private _httpClient: HttpClient) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get artists$(): Observable<Artist[]> {
        return this._artists.asObservable();
    }

    get paginatedArtists$(): Observable<PaginatedData<Artist>> {
        return this._paginatedArtists.asObservable();
    }

    get paginatedLabels$(): Observable<PaginatedData<Label>> {
        return this._paginatedLabels.asObservable();
    }

    get labels$(): Observable<Label[]> {
        return this._labels.asObservable();
    }

    get genres$(): Observable<Genre[]> {
        return this._genres.asObservable();
    }

    get stores$(): Observable<Store[]> {
        return this._stores.asObservable();
    }

    get pagination$(): Observable<Pagination> {
        return this._pagination.asObservable();
    }

    get release$(): Observable<Release> {
        return this._release.asObservable();
    }

    get releases$(): Observable<Release[]> {
        return this._releases.asObservable();
    }

    getArtists(releaseId?: string): Observable<Artist[]> {
        //TODO Only request data if the _artists subject has none
        let params;
        if (releaseId) {
            params = {
                releaseUuid: releaseId
            }
        }
        let observable = this._httpClient.get<Artist[]>('api/release/artists', { params });
        observable.subscribe((artists) => {
            this._artists.next(artists.sort((a, b) => a.title > b.title ? 1 : -1));
        })
        return observable;
    }

    getPaginatedArtists(page: number = 0, size: number = 10, sort: string = 'createdAt', order: 'asc' | 'desc' | '' = 'desc', search?: string, userId?: string):
        Observable<PaginatedData<Artist>> {
        let params = {
            page: '' + page,
            size: '' + size,
            sort,
            order,
        }
        if (search) {
            params['search'] = search;
        }

        if (userId) {
            params['userId'] = userId;
        }

        return this._httpClient.get<PaginatedData<Artist>>('api/release/paginated-artists', { params }).pipe(
            tap((response: PaginatedData<Artist>) => {
                this._paginatedArtists.next(response);
            })
        );
    }

    getPaginatedLabels(page: number = 0, size: number = 10, sort: string = 'createdAt', order: 'asc' | 'desc' | '' = 'desc', search?: string, userId?: string):
        Observable<PaginatedData<Label>> {
        let params = {
            page: '' + page,
            size: '' + size,
            sort,
            order,
        }
        if (search) {
            params['search'] = search;
        }

        if (userId) {
            params['userId'] = userId;
        }

        return this._httpClient.get<PaginatedData<Label>>('api/release/paginated-labels', { params }).pipe(
            tap((response: PaginatedData<Label>) => {
                this._paginatedLabels.next(response);
            })
        );
    }


    createArtist(artist: Artist, userUuid?: string): Observable<Artist> {
        let params = {};
        if (userUuid) {
            params['user'] = userUuid;
        }
        return this.artists$.pipe(
            take(1),
            switchMap(artists => this._httpClient.post<Artist>('api/release/artist', artist, { params: params }).pipe(
                map((newArtist) => {
                    if (artists) {
                        this._artists.next([...artists, newArtist]);
                    }
                    return newArtist;
                })
            ))
        );
    }

    updateArtist(id: string, artist: Artist): Observable<Artist> {
        return this.artists$.pipe(
            take(1),
            switchMap(artists => this._httpClient.patch<Artist>('api/common/releases/artist', {
                id,
                artist
            }).pipe(
                map((updatedEntity) => {
                    const index = artists.findIndex(item => item.uuid === id);
                    artists[index] = updatedEntity;
                    this._artists.next(artists);
                    return updatedEntity;
                })
            ))
        );
    }

    updateLabel(id: string, label: Partial<Label>): Observable<Label> {
        return this.labels$.pipe(
            take(1),
            switchMap(labels => this._httpClient.post<Label>('api/release/label/by-uuid/' + id, label).pipe(
                map((updatedEntity) => {
                    if (labels) {
                        const index = labels.findIndex(item => item.uuid === id);
                        labels[index] = updatedEntity;
                        this._labels.next(labels);
                    }
                    return updatedEntity;
                })
            ))
        );
    }

    createLabel(label: Label, userUuid?: string): Observable<Label> {
        let params = {};
        if (userUuid) {
            params['user'] = userUuid;
        }
        return this.labels$.pipe(
            take(1),
            switchMap(labels => this._httpClient.post<Label>('api/release/label/new', label, { params: params }).pipe(
                map((newLabel) => {
                    if (labels) {
                        this._labels.next([...labels, newLabel]);
                    }
                    return newLabel;
                })
            ))
        );
    }

    deleteArtist(id: string): Observable<boolean> {
        if (!id) {
            console.error('No id provided');
            return of(false);
        }
        return combineLatest([this.artists$, this.paginatedArtists$]).pipe(
            take(1),
            switchMap(([artists, paginatedArtists]) => this._httpClient.delete('api/release/artist/by-uuid/' + id).pipe(
                map((isDeleted: boolean) => {
                    if (isDeleted) {
                        if (artists) {
                            let index = artists.findIndex(item => item.uuid === id);
                            artists.splice(index, 1);
                            this._artists.next(artists);
                        }

                        if (paginatedArtists) {
                            let index = paginatedArtists.data.findIndex(item => item.uuid === id);
                            paginatedArtists.data.splice(index, 1);
                            paginatedArtists.pagination.totalLength--;
                            this._paginatedArtists.next(paginatedArtists);
                        }
                    }

                    return isDeleted;
                }),
            ))
        );
    }

    searchArtist(search: string, store: "spotify" | "apple"): Observable<ArtistSearchResult[]> {
        return this._httpClient.get<ArtistSearchResult[]>('api/release/search-artists', {
            params: {
                search,
                store
            }
        });
    }

    deleteLabel(id: string): Observable<boolean> {
        if (!id) {
            console.error('No id provided');
            return of(false);
        }
        return combineLatest([this.labels$, this.paginatedLabels$]).pipe(
            take(1),
            switchMap(([labels, paginatedLabels]) => this._httpClient.delete('api/release/label/by-uuid/' + id).pipe(
                map((isDeleted: boolean) => {
                    if (isDeleted) {
                        if (labels) {
                            let index = labels.findIndex(item => item.uuid === id);
                            labels.splice(index, 1);
                            this._labels.next(labels);
                        }

                        if (paginatedLabels) {
                            let index = paginatedLabels.data.findIndex(item => item.uuid === id);
                            paginatedLabels.data.splice(index, 1);
                            paginatedLabels.pagination.totalLength--;
                            this._paginatedLabels.next(paginatedLabels);
                        }
                    }

                    return isDeleted;
                }),
            ))
        );
    }

    getLabels(releaseId?: string): Observable<Label[]> {
        let params;
        if (releaseId) {
            params = {
                releaseUuid: releaseId
            }
        }
        return this._httpClient.get<Label[]>('api/release/labels', { params }).pipe(
            tap((labels) => {
                this._labels.next(labels);
            })
        );
    }

    getGenres(): Observable<Genre[]> {
        return this._httpClient.get<Genre[]>('api/release/genres').pipe(
            tap((genres) => {
                this._genres.next(genres);
            })
        );
    }

    getStores(releaseId?: string): Observable<Store[]> {
        //TODO: This breaks user switching (for example sing-in-as-user)
        /* if (this._stores?.value?.length) {
            return of(this._stores.value);
        } */
        let params;
        if (releaseId) {
            params = {
                releaseUuid: releaseId
            }
        }
        return this._httpClient.get<Store[]>('api/release/stores', { params }).pipe(
            tap((stores) => {
                this._stores.next(stores);
            })
        );
    }

    getReleases(): Observable<Release[]> {
        return this._httpClient.get<Release[]>('api/release/all').pipe(
            tap((releases) => {
                this._releases.next(releases);
            })
        );
    }

    getPaginatedReleases(page: number = 0, size: number = 10, sort: string = 'releaseDate', order: 'asc' | 'desc' | '' = 'desc', search: string = '', labelUuid: string = undefined, status: string = undefined):
        Observable<PaginatedData<Release>> {
        return this._httpClient.get<PaginatedData<Release>>('api/release/paginated', {
            params: {
                page: '' + page,
                size: '' + size,
                sort,
                order,
                search,
                label: labelUuid,
                status
            }
        }).pipe(
            tap((response) => {
                this._pagination.next(response.pagination);
                this._releases.next(response.data);
            })
        );
    }

    getReleaseById(id: string): Observable<Release> {
        return this._httpClient.get<Release>('api/release/by-uuid/' + id).pipe(
            tap((release) => {
                this._release.next(release);
            })
        );
    }


    createRelease(): Observable<Release> {
        return this.releases$.pipe(
            take(1),
            switchMap(releases => this._httpClient.post<Release>('api/release/new', {}).pipe(
                map((newRelease) => {
                    this._releases.next([newRelease, ...releases]);
                    return newRelease;
                })
            ))
        );
    }

    updateRelease(release: Release, publish?: boolean): Observable<Release> {
        var dto = {
            release,
            publish,
        }
        return this.releases$.pipe(
            take(1),
            switchMap(releases => this._httpClient.post<Release>('api/release', dto).pipe(
                map((updatedRelease) => {
                    if (!releases) {
                        return updatedRelease;
                    }

                    const index = releases.findIndex(item => item.uuid === release.uuid);
                    releases[index] = updatedRelease;
                    this._releases.next(releases);
                    return updatedRelease;
                }),
                switchMap(updatedRelease => this.release$.pipe(
                    take(1),
                    filter(item => item && item.uuid === release.uuid),
                    tap(() => {
                        //Saving the release can return a new label, add it to the list of known labels
                        if (updatedRelease.label?.uuid) {
                            this.labels$.pipe(take(1)).subscribe(labels => {
                                if (labels.map(label => label.uuid).find(uuid => uuid === updatedRelease.label.uuid) === undefined) {
                                    labels.push(updatedRelease.label);
                                    this._labels.next(labels);
                                }
                            });
                        }
                        this._release.next(updatedRelease);
                        return updatedRelease;
                    })
                ))
            ))
        );
    }

    deleteRelease(id: string): Observable<boolean> {
        return this.releases$.pipe(
            take(1),
            switchMap(releases => this._httpClient.delete('api/release/by-uuid/' + id).pipe(
                map((isDeleted: any) => {
                    const index = releases.findIndex(item => item.uuid === id);
                    releases.splice(index, 1);
                    this._releases.next(releases);
                    return isDeleted;
                })
            ))
        );
    }

    getUsableSubscriptions(release: Release): Observable<ReleaseSubscription[]> {
        return this._httpClient.post<ReleaseSubscription[]>('api/subscription/usable', release);
    }

    getArtistNamesForRelease(release?: Release, role?: ParticipantRole): string {
        if (!release) {
            return undefined;
        }
        return release.participants?.filter(p => p.participant && p.role === (role ?? ParticipantRole.MainArtist)).map(artist => artist.participant.title).join(', ');
    }

    getArtistNamesForTrack(track?: Track, role?: ParticipantRole): string {
        if (!track) {
            return undefined;
        }
        return track.participants?.filter(p => p.participant && p.role === (role ?? ParticipantRole.MainArtist)).map(artist => artist.participant.title).join(', ');
    }
}
